import { CSSProperties, useEffect, useRef, useState } from 'react';
import Modal from '../../../components/Modal';
import ClipRangeTrack from './ClipRangeTrack';
import { captureVersionClip, uploadVersionFrame } from '../../../api';
import { MAX_CLIP_SECONDS, MIN_CLIP_SECONDS } from '../constants';
import { FRAME_SECONDS, clampTime, clipDurationError, formatSeconds, timeToFrameNumber } from '../utils/clipTime';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface ClipCaptureTarget {
  shotId: string;
  versionId: string;
  videoURL: string;
  // 截取源：擦除字幕版传 'erased'，后端据此改用擦除产物做片段截取（单帧仍从展示的 videoURL 取帧）
  source?: 'erased';
}

interface ClipCaptureModalProps {
  open: boolean;
  target: ClipCaptureTarget | null;
  // 真人剧且平台支持审核 → 截取前询问是否包含真人内容（与上传流程一致）
  isRealPersonReviewable: boolean;
  t: Translate;
  onClose: () => void;
  onCaptured: (destination: 'review' | 'library') => void;
  onError: (message: string) => void;
}

type ClipMode = 'frame' | 'segment';

const stepButtonStyle: CSSProperties = {
  background: 'none', border: '1px solid var(--border)', borderRadius: '4px',
  cursor: 'pointer', padding: '2px 8px', fontSize: '0.72rem', color: 'var(--text-2)',
};

export default function ClipCaptureModal({
  open, target, isRealPersonReviewable, t, onClose, onCaptured, onError,
}: ClipCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [mode, setMode] = useState<ClipMode>('frame');
  const [duration, setDuration] = useState(0);
  const [videoReady, setVideoReady] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [humanDialogOpen, setHumanDialogOpen] = useState(false);

  // 打开/切换目标时重置全部本地状态
  useEffect(() => {
    if (open) {
      setMode('frame');
      setDuration(0);
      setVideoReady(false);
      setIsSeeking(false);
      setCurrentTime(0);
      setStartTime(0);
      setEndTime(0);
      setName('');
      setSubmitting(false);
      setHumanDialogOpen(false);
    }
  }, [open, target?.versionId]);

  // 处理中拦截页面关闭/刷新：后端是同步接口，断开连接会取消处理导致提交失败
  useEffect(() => {
    if (!submitting) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [submitting]);

  const seekTo = (time: number) => {
    const v = videoRef.current;
    if (!v || duration === 0) return;
    v.currentTime = clampTime(time, duration);
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    setDuration(d);
    setEndTime(clampTime(Math.min(d, MAX_CLIP_SECONDS), d));
  };

  // 单帧截取：canvas 取当前显示帧，所见即所得。
  // 就绪保护：metadata 未加载 / seek 中截取会得到空图或旧帧。
  const captureFrameBlob = (): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const v = videoRef.current;
      if (!v || v.videoWidth === 0 || v.readyState < 2 || v.seeking) {
        reject(new Error(t('clipModal.videoNotReady')));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error(t('clipModal.captureFailed')));
        return;
      }
      try {
        ctx.drawImage(v, 0, 0);
        canvas.toBlob(
          blob => (blob ? resolve(blob) : reject(new Error(t('clipModal.captureFailed')))),
          'image/jpeg', 0.92,
        );
      } catch (err: unknown) {
        // SecurityError：TOS 未配置 CORS，canvas 被跨域污染
        if (err instanceof DOMException && err.name === 'SecurityError') {
          reject(new Error(t('clipModal.corsError')));
        } else {
          reject(err instanceof Error ? err : new Error(t('clipModal.captureFailed')));
        }
      }
    });

  const trimmedName = name.trim();

  // crossOrigin 加载必须用带参 URL 与普通播放的缓存隔离：同一 URL 曾被站内
  // 不带 crossOrigin 的 <video> 播放过时，浏览器缓存里是无 CORS 头的响应，
  // 直接复用会报 "No 'Access-Control-Allow-Origin' header"（部分用户报错的根因）
  const corsVideoURL = target
    ? `${target.videoURL}${target.videoURL.includes('?') ? '&' : '?'}xo=1`
    : undefined;

  const doSubmit = async (containsHuman: boolean) => {
    if (!target) return;
    setHumanDialogOpen(false);
    setSubmitting(true);
    try {
      let result;
      if (mode === 'frame') {
        const blob = await captureFrameBlob();
        result = await uploadVersionFrame(target.shotId, target.versionId, blob, {
          containsHuman, name: trimmedName,
        });
      } else {
        result = await captureVersionClip(target.shotId, target.versionId, {
          startTime, endTime, containsHuman, name: trimmedName, source: target.source,
        });
      }
      onCaptured(result.destination);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : t('clipModal.captureFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCaptureClick = () => {
    if (isRealPersonReviewable) {
      setHumanDialogOpen(true);
    } else {
      doSubmit(false);
    }
  };

  const durationErr = clipDurationError(startTime, endTime);
  const durationErrText = durationErr === 'invalid'
    ? t('clipModal.durationInvalid')
    : durationErr === 'tooShort'
      ? t('clipModal.durationTooShort', { min: MIN_CLIP_SECONDS })
      : durationErr === 'tooLong'
        ? t('clipModal.durationTooLong', { max: MAX_CLIP_SECONDS })
        : '';
  // 单帧模式按钮禁用条件须覆盖 seek 中与 metadata 未加载（spec 就绪保护），
  // captureFrameBlob 内的检查仅作兜底；素材名称必填
  const frameReady = videoReady && !isSeeking && (videoRef.current?.videoWidth ?? 0) > 0;
  const canCapture = !submitting && trimmedName !== '' &&
    (mode === 'frame' ? frameReady : (videoReady && !durationErr));

  // 单帧模式拖动游标：本地 currentTime 即时跟手，视频异步 seek 到位
  const seekAndTrack = (v: number) => {
    setCurrentTime(clampTime(v, duration));
    seekTo(v);
  };

  // 片段模式 ±1 帧微调：与时间轴手柄共用同一套钳制（起点不越过终点，反之亦然）
  const adjustStart = (delta: number) => {
    const v = Math.min(clampTime(startTime + delta, duration), clampTime(endTime - 0.1, duration));
    setStartTime(Math.max(0, v));
    seekTo(v);
  };
  const adjustEnd = (delta: number) => {
    const v = Math.max(clampTime(endTime + delta, duration), clampTime(startTime + 0.1, duration));
    setEndTime(v);
    seekTo(v);
  };

  // 秒 + 帧号联合展示：帧号由 CLIP_FRAME_RATE 常量换算（Seedance 24fps）
  const timeWithFrame = (value: number) =>
    t('clipModal.timeWithFrame', { time: formatSeconds(value), frame: timeToFrameNumber(value) });

  const fineTuneCell = (label: string, value: number, adjust: (delta: number) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <span style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontSize: '0.78rem', color: 'var(--text)', minWidth: '7.5em', textAlign: 'center' }}>{timeWithFrame(value)}</span>
      <button style={stepButtonStyle} disabled={submitting} onClick={() => adjust(-FRAME_SECONDS)}>{t('clipModal.minusOneFrame')}</button>
      <button style={stepButtonStyle} disabled={submitting} onClick={() => adjust(FRAME_SECONDS)}>{t('clipModal.plusOneFrame')}</button>
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={t('clipModal.title')}
      titleExtra={
        <span style={{
          padding: '3px 10px', fontSize: '0.75rem', lineHeight: 1.5,
          color: '#854d0e', background: '#fef9c3',
          border: '1px solid #facc15', borderRadius: '4px',
        }}>
          {t('clipModal.usageHint')}
        </span>
      }
      width="720px"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span style={{ fontSize: '0.78rem', color: durationErr && mode === 'segment' ? 'var(--danger)' : 'var(--text-3)' }}>
            {mode === 'segment'
              ? (durationErrText || t('clipModal.duration', { duration: formatSeconds(endTime - startTime) }))
              : t('clipModal.currentTime', { time: timeWithFrame(currentTime) })}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              className="btn"
              style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}
              disabled={!canCapture}
              onClick={handleCaptureClick}
            >
              {submitting
                ? t('clipModal.capturing')
                : t(mode === 'frame' ? 'clipModal.captureFrame' : 'clipModal.captureClip')}
            </button>
          </div>
        </div>
      }
    >
      {/* 分类 tab：截取单帧 / 截取片段 */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '0.75rem' }}>
        {(['frame', 'segment'] as ClipMode[]).map(m => (
          <button
            key={m}
            onClick={() => { if (submitting) return; setMode(m); setHumanDialogOpen(false); }}
            style={{
              padding: '6px 16px', background: 'none', border: 'none',
              borderBottom: mode === m ? '2px solid var(--theme-storyboard)' : '2px solid transparent',
              color: mode === m ? 'var(--theme-storyboard)' : 'var(--text-2)',
              fontWeight: mode === m ? 600 : 400, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '-1px',
            }}
          >
            {t(m === 'frame' ? 'clipModal.frameTab' : 'clipModal.segmentTab')}
          </button>
        ))}
      </div>

      {/* 视频：crossOrigin 必须设置，canvas 截帧需要 TOS CORS 放行 */}
      <video
        ref={videoRef}
        src={corsVideoURL}
        crossOrigin="anonymous"
        controls
        style={{ width: '100%', maxHeight: '360px', background: '#000', borderRadius: '6px' }}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={() => setVideoReady(true)}
        onSeeking={() => setIsSeeking(true)}
        onSeeked={() => setIsSeeking(false)}
        onTimeUpdate={e => setCurrentTime(clampTime((e.target as HTMLVideoElement).currentTime, duration))}
      />

      {/* 时间线（帧精度）：编辑器式单条时间轴，单帧为单游标变体，片段为选区变体 */}
      {mode === 'frame'
        ? (
          <>
            <ClipRangeTrack
              variant="single"
              duration={duration}
              currentTime={currentTime}
              disabled={submitting || !videoReady}
              onSeek={seekAndTrack}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
              {fineTuneCell(t('clipModal.currentTime', { time: '' }).trim(), currentTime, delta => seekAndTrack(currentTime + delta))}
            </div>
          </>
        )
        : (
          <>
            <ClipRangeTrack
              duration={duration}
              startTime={startTime}
              endTime={endTime}
              currentTime={currentTime}
              disabled={submitting || !videoReady}
              onChangeStart={setStartTime}
              onChangeEnd={setEndTime}
              onSeek={seekTo}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.4rem', flexWrap: 'wrap', gap: '0.4rem' }}>
              {fineTuneCell(t('clipModal.startTime'), startTime, adjustStart)}
              {fineTuneCell(t('clipModal.endTime'), endTime, adjustEnd)}
            </div>
          </>
        )}

      {/* 素材名称（必填，留空时截取按钮禁用） */}
      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={t('clipModal.namePlaceholder')}
        style={{
          width: '100%', marginTop: '0.75rem', padding: '8px 12px', boxSizing: 'border-box',
          border: `1px solid ${trimmedName === '' ? 'var(--danger, #dc2626)' : 'var(--border)'}`,
          borderRadius: 'var(--radius)', fontSize: '0.85rem',
        }}
      />

      {/* 处理中提示：同步接口，关闭页面会中断后端处理 */}
      {submitting && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          marginTop: '0.75rem', padding: '8px 12px', borderRadius: '6px',
          background: '#fef9c3', border: '1px solid #facc15',
          fontSize: '0.8rem', color: '#854d0e',
        }}>
          <span className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', flexShrink: 0 }}></span>
          {t('clipModal.processingHint')}
        </div>
      )}
    </Modal>

    {/* 真人内容询问弹窗（真人剧且平台支持审核时）：写明审核原因再让用户选择 */}
    <Modal
      open={humanDialogOpen}
      onClose={() => !submitting && setHumanDialogOpen(false)}
      title={t('clipModal.humanQuestion')}
      width="480px"
      footer={
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', width: '100%' }}>
          <button className="btn btn-secondary" onClick={() => setHumanDialogOpen(false)} disabled={submitting}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button className="btn btn-secondary" onClick={() => doSubmit(false)} disabled={submitting}>
            {t('clipModal.noHuman')}
          </button>
          <button
            className="btn"
            style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}
            onClick={() => doSubmit(true)}
            disabled={submitting}
          >
            {t('clipModal.hasHuman')}
          </button>
        </div>
      }
    >
      <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--text-2)' }}>
        {t('clipModal.humanQuestionDesc')}
      </p>
    </Modal>
    </>
  );
}
